import { create } from "zustand";
import { subscribeContact } from "@/lib/email";
import { newsletterSchema } from "@/lib/validations";

interface SubscriptionState {
	email: string;
	isSubscribed: boolean;
	alreadySubscribed: boolean;
	isSubmitting: boolean;
	error: string | null;

	setEmail: (email: string) => void;
	subscribe: () => Promise<void>;
	reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
	email: "",
	isSubscribed: false,
	alreadySubscribed: false,
	isSubmitting: false,
	error: null,

	setEmail: (email) => set({ email, error: null }),

	subscribe: async () => {
		const { email, isSubmitting } = get();
		if (isSubmitting) return;

		const validation = newsletterSchema.safeParse({ email: email.trim() });
		if (!validation.success) {
			set({ error: validation.error.issues[0]?.message ?? "Invalid email" });
			return;
		}

		set({ isSubmitting: true, error: null, alreadySubscribed: false });

		try {
			const result = await subscribeContact(
				undefined,
				email.trim(),
				undefined,
				["footer-newsletter"],
			);

			if (!result.success) {
				throw new Error("Failed to subscribe. Please try again.");
			}

			if (typeof window !== "undefined") {
				localStorage.setItem("iam_subscriber_email", email.trim());
				localStorage.setItem("iam_subscribed_at", new Date().toISOString());
			}

			set({
				isSubscribed: true,
				alreadySubscribed: result.duplicate,
				email: "",
				isSubmitting: false,
			});
		} catch (error) {
			set({
				error: error instanceof Error ? error.message : "Failed to subscribe",
				isSubmitting: false,
			});
		}
	},

	reset: () =>
		set({
			email: "",
			isSubscribed: false,
			alreadySubscribed: false,
			isSubmitting: false,
			error: null,
		}),
}));
